import React, { useEffect, useState } from 'react';
import t from 'utils/translationHelper';

import { BaseDrawer, LineBreak } from '@alpha/ui-lib/ui/Drawer/APBaseDrawer';
import { StyledKeyValueTable } from '@alpha/ui-lib/ui/table/StyledKeyValueTable';
import { Typography } from '@alpha/ui-lib/ui/Typography';

import { formatCurrency } from '../../../../utils/currency.helpers';
import { formatCurrencyAccountBankCode } from '../../../../utils/formatCurrencyAccountBankCode';
import { ISelectedAccount } from '../AccountInfoDrawer';
import { TCurrencyAccount } from 'models/currencyAccounts';
import { faArrowRight, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useStyles from './index.styles';
import { Link } from 'react-router-dom';
import routes from 'routes.path';
import useLog from 'hooks/useLog';
import { DateTime } from 'luxon';

interface IBody {
  selectedAccount: ISelectedAccount,
  eMoneyDisabled?: boolean;
}

const CurrencyAccountInfo: React.FC<IBody> = (props: IBody) => {
  const { selectedAccount, eMoneyDisabled } = props;
  const classes = useStyles();
  const { logError } = useLog();
  const [corpayMigrationStartDate, setCorpayMigrationStartDate] = useState<string | undefined>();

  const details = [
    {
      key: t('currency'),
      value: selectedAccount.account ? selectedAccount.account.currencyCode : '-',
      boldValue: true,
    },
    { key: t('bank_name'), value: selectedAccount.account.bankName || '-' },
    { key: t('swift/bic'), value: selectedAccount.account.swift || '-' },
    {
      key: t('iban'),
      value: selectedAccount.account.iban
        ? selectedAccount.account.iban.toUpperCase()
        : '-',
    },
    { key: t('account_number'), value: selectedAccount.account.accountNumber || '-' },
    { key: t('national_bank_code'), value: selectedAccount.account.sortCode ? formatCurrencyAccountBankCode(selectedAccount.account.sortCode, selectedAccount.account.countryCode) : '-' },
    { key: t('account_name'), value: selectedAccount.account.accountName || '-' },
    selectedAccount.account.type === 'Wallet'
      ? { key: t('reference_IT_'), value: selectedAccount.account.reference || '-' }
      : { key: '', value: '' },
  ];

  const corpayRequiredFields = (account: TCurrencyAccount) => {
    return account.corpayAccountReference && account.corpayAccountCurrency && !!corpayMigrationStartDate && DateTime.fromISO(corpayMigrationStartDate) <= DateTime.now();
  };

  useEffect(() => {
    try {
      const accInfo = localStorage.getItem('account-info');
      if (accInfo) {
        const startDate = JSON.parse(accInfo).corpayMigrationStartDate;
        setCorpayMigrationStartDate(startDate);
      }
    } catch(error) {
      logError({ action: 'Error loading account info', error });
    }
  }, []);

  return (
    <div>
      {corpayRequiredFields(selectedAccount.account) ? (
        <div className={classes.migrationAlert}>
          <div className={classes.migrationAlertContent}>
            <FontAwesomeIcon icon={faInfoCircle} className={classes.migrationAlertIcon} />
            <div>
              <div className={classes.migrationAlertHeader}>
                <Typography variant="h4">{t('account_details_updated')}</Typography>
              </div>
              <div className={classes.migrationAlertBody}>
                <Typography variant="h4">{t('your_account_has_been_successfully_migrated')}</Typography>
              </div>
            </div>
          </div>
          <Link to={routes.migrationReadiness.base} className={classes.migrationAlertCta}>
            <Typography variant="h4">{t('go_to_the_migration_centre')}</Typography>
            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
          </Link>
        </div>
      ) : (
        <>
          {!eMoneyDisabled && (
            <>
              <div className={classes.balanceContainer}>
                <BaseDrawer.Body>
                  <div className={classes.balanceRow}>
                    <Typography variant="h4">
                      {t('cleared_balance')}
                    </Typography>
                    <Typography variant="subtitle1" className={classes.balanceValue}>
                      {formatCurrency(selectedAccount?.account.currencyCode,
                        selectedAccount?.account.clearedBalance)}
                    </Typography>
                  </div>
                </BaseDrawer.Body>
                <LineBreak />
              </div>
            </>
          )}
          <BaseDrawer.Body>
            <>
              <div className={classes.bankDetailsHeader}>
                <Typography variant="h3" className={classes.bankDetailsTitle}>
                  {t('alpha_fx_bank_details')}
                </Typography>
                <Typography variant="h4">
                  {t('details_to_pay_into_for_settlement_of_trades')}
                </Typography>
              </div>
              <div className={classes.bankDetailsTable}>
                <StyledKeyValueTable testId="account-drawer-body" data={details} />
              </div>
            </>
          </BaseDrawer.Body>
        </>
      )}
    </div>
  );
};

export default CurrencyAccountInfo;
